import dayjs from "dayjs";
import type { Organization } from "../../../api/v1";
import { isReceiptsModuleEnabled } from "../../../utils/isReceiptsModuleEnabled";
import { isCommercialUsageType } from "../../libs/utils";
import { generateTorRulesSets, torRulesOption } from "../BookingForm/utils";
import type { Invoice } from "./PostDatedCheques/types";

export const paymentModeOptions = [
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "credit_card", label: "Credit Card" },
  { value: "directDebit", label: "Direct Debit" },
];

export const visibilityOptions = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "bounce", label: "Bounce" },
  { value: "settled", label: "Settled" },
  { value: "cancelled", label: "Cancel" },
  { value: "transfer_in_progress", label: "Transfer In Progress" },
];

export const bouncedReasons = [
  { value: "", label: "Select" },
  { value: "signature_mismatch", label: "Signature mismatch" },
  { value: "insufficient_funds", label: "Insufficient funds" },
  { value: "damaged_cheque", label: "Damaged cheque" },
  { value: "overwriting_on_cheque ", label: "Overwriting on cheque " },
  { value: "incorrect_amount", label: "Incorrect amount" },
];

export const removeEmptyValues = (obj) => {
  for (const key in obj) {
    if (obj[key] === "") {
      delete obj[key];
    }
  }
  return obj;
};

export const defaultFormVal = {
  status: { value: "pending", label: "Pending" },
  paymentMode: "",
  paymentRef: "",
  bankName: null,
  bankNameRef: "",
  paymentRefReplaced: "",
  reason: { value: "signature_mismatch ", label: "Signature mismatch" },
  paymentStatus: { value: "pending", label: "Pending" },
  paymentModeType: { value: "cheque", label: "Cheque" },
  depositAccount: "",
  depositBank: "",
  bouncedChequePaymentMode: { value: "", label: "" },
  bouncedChequeFee: 0,
  paymentMethodType: { label: "Full", value: 1 },
  totalBalance: 0,
  amountToPay: 0,
  dueAmount: 0,
  depositTo: { value: "", label: "" },
  subPdcRef: { value: undefined, label: undefined },
  invoicePeriod: "",
  bounceDate: null,
  chequeDate: null,
  swiftCode: "",
  branch: "",
  address: "",
  paymentNote: "",
};

export const deferDefaultFormVal = {
  paymentModeType: { value: "cheque", label: "Cheque" },
  paymentStatus: { value: "pending", label: "Pending" },
  paymentRef: "",
  isApplyDefferalFee: false,
  deferralFee: 0,
  vat: 0,
  totalDeferralFee: 0,
  defeReason: "",
};

export const paymentModeRendering = (data) => {
  if (data?.params?.data?.description === "landlord_payable") {
    return paymentModeOptions?.filter((item) => item?.value !== "credit_card");
  } else {
    return paymentModeOptions;
  }
};

const isFutureDate = (current: string) => {
  const currentDate = dayjs(current);
  return currentDate.isAfter(dayjs(), "day");
};

/** Receipt-only action values — stripped from Invoicing dropdowns (shown on Receipts instead). */
const RECEIPT_SURFACE_ACTION_VALUES = new Set([
  "mark_as_bounced",
  "mark_cleared",
  "defer_cheque_deposit",
  "approve_pending_request",
  "reject_pending_request",
  "recallCheque",
  "approveRejectRecallCheque",
  "approveRejectReceipt",
]);

export const isReceiptPendingConfirmation = (row: any): boolean =>
  row?.receiptApprovalRequest?.toLowerCase() === "pending" ||
  (Array.isArray(row?.receiptApprovals) &&
    row.receiptApprovals.some((receipt: { status?: string }) => receipt?.status === "pending_confirmation"));

/** Invoicing uses `requirePdcPending: true`; receipts list uses `false`. */
export const shouldShowApproveRejectReceipt = (
  row: any,
  organization?: Organization,
  canApproveReceipts?: boolean,
  requirePdcPending = true,
): boolean => {
  if (!Boolean(organization?.twoStepReceiptConfirmation) || !canApproveReceipts) {
    return false;
  }

  if (!isReceiptPendingConfirmation(row)) {
    return false;
  }
  if (requirePdcPending) {
    const rawStatus = row?.chequeStatus ?? row?.paymentStatus ?? "";
    const chequeStatus = typeof rawStatus === "string" ? rawStatus.trim().toLowerCase() : "";
    if (chequeStatus !== "pending") {
      return false;
    }
  }
  return true;
};

/** Hide Receipts-only actions on Invoicing (they belong on the Receipts screen). */
function stripReceiptOnlyActionsFromInvoicing<T extends { value: string }>(options: T[]): T[] {
  return options.filter((o) => !RECEIPT_SURFACE_ACTION_VALUES.has(o.value));
}

export const setActionOptions = (
  data: any,
  organization?: Organization,
  canChequeCleared?: boolean,
  canApproveReceipts?: boolean,
  actionSurface: "invoicing" | "receipts" = "invoicing",
) => {
  const {
    balance,
    chequeDeferral,
    creditNoteRequest,
    deferred,
    deferredRequest,
    depositDate,
    propertyDetails,
    recallChequeRequestStatus,
    torType,
    receiptNo,
    subPdcs,
  } = (data?.data as Invoice) || {};

  const isRecallChequePending = recallChequeRequestStatus === "pending";
  const hasReceiptNo = Boolean(receiptNo) || (subPdcs?.length > 0 && subPdcs.some((pdc) => pdc?.receiptNo));
  const rawMode = data?.data?.modeOfPayment ?? data?.data?.paymentMode;
  const mode = typeof rawMode === "string" ? rawMode.trim().toLowerCase().replace(/\s+/g, "_") : "";

  const rawChequeStatus = data?.data?.chequeStatus ?? data?.data?.paymentStatus;
  let chequeStatus = typeof rawChequeStatus === "string" ? rawChequeStatus.trim().toLowerCase() : "";
  if (!chequeStatus) chequeStatus = "pending";
  if (chequeStatus === "bounced") chequeStatus = "bounce";

  // Payment type flags
  const isCheque = mode === "cheque";
  const isCash = mode === "cash";
  const isDirectDebit = mode === "directdebit";
  const isCreditCard = ["credit_card", "credit card"].includes(mode);
  const isBankTransfer = ["bank_transfer", "bank transfer"].includes(mode);
  const deferralApproval = chequeDeferral?.approval?.toLowerCase() === "enable" || false;
  const isDeferred = deferred && deferralApproval && deferredRequest === "pending";
  const isCreditNoteRequestPending = creditNoteRequest === "pending";
  const isCreditNoteRequestApproved = creditNoteRequest === "approved";
  const enableChequeClearance = Boolean(organization?.enableChequeClearance);
  let isDeferredStatus = chequeStatus === "deferred";
  let isCheckStatusBounced = chequeStatus === "bounce";
  let isCleared = data?.data?.isCleared;
  let isReceiptNoGenerated = Boolean(
    data?.data?.receiptNo ?? (data?.data as { receiptNumber?: string })?.receiptNumber,
  );

  let isPartial = data?.data?.paymentMethodType?.toLowerCase() === "partial";
  let isApproveReceipt = shouldShowApproveRejectReceipt(data?.data, organization, canApproveReceipts, true);

  const isCommercial = isCommercialUsageType(propertyDetails?.usagesType);

  let subPdcsPartial =
    data?.data?.subPdcs?.length > 0 &&
    data?.data?.subPdcs?.find(
      (pdc) => pdc?.paymentMode?.toLowerCase() === "cheque" && pdc?.status?.toLowerCase() === "paid",
    ) &&
    isPartial;

  const isDraft = chequeStatus === "draft";

  const ACTIONS = {
    edit: {
      id: "edit",
      value: "edit",
      label: "Edit",
      hidden: Boolean(isDeferred || isDeferredStatus || isCleared || isCreditNoteRequestPending || isApproveReceipt),
    },
    markcleared: {
      id: "mark_cleared",
      value: "mark_cleared",
      label: "Mark as Cleared",
      hidden: Boolean(
        !isReceiptNoGenerated ||
        isCleared ||
        !enableChequeClearance ||
        isCreditNoteRequestPending ||
        !canChequeCleared ||
        isApproveReceipt,
      ),
    },
    receivePayment: {
      id: "receivePayment",
      value: "confirm_payment",
      label: "Receive Payment",
      hidden: Boolean(
        isFutureDate(depositDate) ||
        isDeferred ||
        isDeferredStatus ||
        isCreditNoteRequestPending ||
        (isApproveReceipt && !isPartial),
      ),
    },
    markAsBounced: {
      id: "markAsBounced",
      value: "mark_as_bounced",
      label: "Mark as Bounced",
      hidden: Boolean(
        isDeferred ||
        isDeferredStatus ||
        isCleared ||
        isCreditNoteRequestPending ||
        isApproveReceipt ||
        isCheckStatusBounced,
      ),
    },
    cancelPayment: {
      id: "cancelPayment",
      value: "cancelled",
      label: "Cancel Payment",
      hidden: Boolean(isDeferred || isDeferredStatus || isCreditNoteRequestPending || isApproveReceipt),
    },
    replaceCheque: {
      id: "replaceCheque",
      value: "replace_cheque",
      label: "Replace Cheque",
      hidden: Boolean(isDeferred || isDeferredStatus || isCreditNoteRequestPending),
    },
    history: { id: "history", value: "history", label: "History", hidden: false },
    generateInvoiceOrTor: (label: string) => ({
      id: "generateTor",
      value: "generateTor",
      label,
      hidden: false,
    }),
    viewTorLedger: {
      id: "viewTorLedger",
      value: "viewTorLedger",
      label: "View TOR Ledger",
      hidden: false,
    },
    printInvoice: {
      id: "printInvoice",
      value: "printInvoice",
      label: "Print Invoice",
      // hidden: Boolean(isCleared),
      hidden: false,
    },
    applyCreditNote: { id: "applyCreditNote", value: "applyCreditNote", label: "Apply Credit Note", hidden: false },
    approveOrRejectCreditNote: {
      id: "approveOrRejectCreditNote",
      value: "approveOrRejectCreditNote",
      label: "Approve/Reject Credit Note",
      hidden: false,
    },
    approvePendingRequest: {
      id: "approve_pending_request",
      value: "approve_pending_request",
      label: "Approve Pending Request",
      hidden: false,
    },
    rejectPendingRequest: {
      id: "reject_pending_request",
      value: "reject_pending_request",
      label: "Reject Pending Request",
      hidden: false,
    },
    deferChequeDeposit: {
      id: "defer_cheque_deposit",
      value: "defer_cheque_deposit",
      label: "Defer Cheque Deposit",
      hidden: isApproveReceipt,
    },
    rejectTransfer: {
      id: "rejectTransfer",
      value: "rejectTransfer",
      label: "Reject Transfer",
      hidden: false,
    },
    recallCheque: {
      id: "recallCheque",
      value: "recallCheque",
      label: "Recall Cheque",
      hidden: Boolean(
        !isCheque ||
        isCleared ||
        isDeferredStatus ||
        isDeferred ||
        isCheckStatusBounced ||
        isRecallChequePending ||
        isApproveReceipt ||
        isCreditNoteRequestPending ||
        (isCreditNoteRequestApproved && !balance) ||
        !hasReceiptNo,
      ),
    },
    approveRejectRecallCheque: {
      id: "approveRejectRecallCheque",
      value: "approveRejectRecallCheque",
      label: "Approve/Reject Recall Cheque",
      hidden: !isRecallChequePending,
    },
    approveRejectReceipt: {
      id: "approveRejectReceipt",
      value: "approveRejectReceipt",
      label: "Approve/Reject Receipt",
      hidden: !isApproveReceipt,
    },
  };

  /** Receipts list is PAID-only from API — Bounce / Clear / Defer / Recall without invoicing status branches; recall vs approve/reject mirrors Invoicing. */
  if (actionSurface === "receipts") {
    return [
      { ...ACTIONS.markAsBounced, hidden: isCheckStatusBounced },
      { ...ACTIONS.markcleared, hidden: false },
      { ...ACTIONS.deferChequeDeposit, hidden: false },
      { ...ACTIONS.recallCheque, hidden: isRecallChequePending },
      { ...ACTIONS.approveRejectRecallCheque, hidden: !isRecallChequePending },
    ];
  }

  const { turnoverAllowedTorValues } = generateTorRulesSets(torRulesOption);
  const hasValidTorType = torType != null && turnoverAllowedTorValues.has(torType);

  const applyCreditNoteActions =
    Boolean(organization?.enableCreditNotes) && !isDeferred
      ? isCreditNoteRequestPending
        ? [ACTIONS.approveOrRejectCreditNote]
        : [ACTIONS.applyCreditNote]
      : [];

  const invoicingActions = [
    ...(isCommercial && hasValidTorType
      ? [ACTIONS.generateInvoiceOrTor(`${isDraft ? "Generate" : "Regenerate"} Invoice`), ACTIONS.viewTorLedger]
      : []),
    ACTIONS.printInvoice,
  ];

  const deferActions =
    !isCreditNoteRequestPending && isCheque
      ? [...(isDeferred ? [ACTIONS.approvePendingRequest, ACTIONS.rejectPendingRequest] : [ACTIONS.deferChequeDeposit])]
      : [];

  const OPTIONS = {
    base: [ACTIONS.edit, ...invoicingActions, ACTIONS.history],
    cancel: [
      ACTIONS.edit,
      ACTIONS.receivePayment,
      ...applyCreditNoteActions,
      ...deferActions,
      ACTIONS.cancelPayment,
      ACTIONS.recallCheque,
      ACTIONS.approveRejectRecallCheque,
      ACTIONS.approveRejectReceipt,
      ...invoicingActions,
      ACTIONS.history,
    ],
    paidCheque: [
      ACTIONS.edit,
      ACTIONS.markAsBounced,
      ACTIONS.markcleared,
      ACTIONS.recallCheque,
      ACTIONS.approveRejectRecallCheque,
      ...invoicingActions,
      ACTIONS.history,
    ],
    paidOther: [ACTIONS.edit, ACTIONS.markcleared, ...invoicingActions, ACTIONS.history],
    bounce: [ACTIONS.replaceCheque, ACTIONS.cancelPayment, ...invoicingActions, ACTIONS.history],
    cancelled: [ACTIONS.history],
    settledCheque: [
      ACTIONS.edit,
      ACTIONS.markAsBounced,
      ACTIONS.recallCheque,
      ACTIONS.approveRejectRecallCheque,
      ...invoicingActions,
      ACTIONS.history,
    ],
    partialBounceCheque: [
      ACTIONS.edit,
      ACTIONS.receivePayment,
      ...applyCreditNoteActions,
      ACTIONS.markAsBounced,
      ...deferActions,
      ACTIONS.markcleared,
      ACTIONS.recallCheque,
      ACTIONS.approveRejectRecallCheque,
      ACTIONS.approveRejectReceipt,
      ...invoicingActions,
      ACTIONS.history,
    ],
    transferInProgress: [
      ACTIONS.edit,
      ACTIONS.receivePayment,
      ACTIONS.rejectTransfer,
      ...invoicingActions,
      ACTIONS.history,
    ],
  };

  // Decision logic (receipts screen returns earlier with a fixed Bounce/Clear/Defer/Recall set)
  let resolved: any[];
  switch (chequeStatus) {
    case "pending":
      if (isCheque || isCash || isBankTransfer || isDirectDebit || isCreditCard) {
        if (subPdcsPartial) {
          resolved = OPTIONS.partialBounceCheque;
        } else {
          resolved = OPTIONS.cancel;
        }
      } else {
        resolved = OPTIONS.base;
      }
      break;

    case "transfer_in_progress":
      resolved = OPTIONS.transferInProgress;
      break;

    case "paid":
      resolved = isCheque ? OPTIONS.paidCheque : OPTIONS.paidOther;
      break;

    case "bounce":
      resolved = isCheque ? OPTIONS.bounce : OPTIONS.base;
      break;

    case "cancelled":
      resolved = OPTIONS.cancelled;
      break;

    case "settled":
      resolved = isCheque ? OPTIONS.settledCheque : OPTIONS.paidOther;
      break;

    default:
      resolved = OPTIONS.base;
  }

  return isReceiptsModuleEnabled(organization) ? stripReceiptOnlyActionsFromInvoicing(resolved) : resolved;
};

export const gridCol = [
  {
    headerName: "Payment Mode",
    field: "modeOfPayment",
    maxWidth: 195,
    width: 165,
    cellRendererFramework: ({ data }) => {
      return data.modeOfPayment;
    },
  },
  {
    headerName: "Status",
    field: "status",
    maxWidth: 110,
    width: 110,
    cellRendererFramework: ({ data }) => `${data.status}d`,
  },
  {
    headerName: "Payment Ref",
    field: "documentNo",
    maxWidth: 195,
    width: 165,
    cellRendererFramework: ({ data }) => data.documentNo,
  },
  {
    headerName: "Bank Name",
    field: "bankName",
    maxWidth: 150,
    width: 150,
    cellRendererFramework: ({ data }) => data.bankName,
  },
];

export const formattedBankList = (configData) => {
  if (configData && configData?.bankList && Object.keys(configData?.bankList)?.length > 0) {
    const bankList = configData?.bankList || {};
    return Object?.entries(bankList as Record<string, string>).map(([_key, label]) => ({
      label,
      value: label.toLowerCase().replace(/\s+/g, "_"),
    }));
  } else {
    return [];
  }
};
